/**
 * @since 2.0.0
 *
 * Represents a value of one of six possible types (a disjoint union).
 *
 * An instance of `DatumEither` is equivalent to `Datum<Either<E, A>>`
 *
 * A common use of `DatumEither` is as a container for dealing with refreshable data values that
 * can have error conditions. The full type list is:
 *
 * - `Initial`
 * - `Pending`
 * - `Refresh<Either<E, A>>`
 *   - `Refresh<Left<E>>`
 *   - `Refresh<Right<A>>`
 * - `Replete<Either<E, A>>`
 *   - `Replete<Left<E>>`
 *   - `Replete<Right<A>>`
 *
 * There are additional helper methods for going from refresh to replete and back.
 */
import { left, isRight, isLeft, right, fromOption as eitherFromOption, fold as eitherFold, } from 'fp-ts/es6/Either';
import { getEitherM } from 'fp-ts/es6/EitherT';
import { pipe, pipeable } from 'fp-ts/es6/pipeable';
import * as D from './Datum';
import { constant, flow } from 'fp-ts/es6/function';
import { sequenceS, sequenceT } from 'fp-ts/es6/Apply';
/**
 * @since 2.0.0
 */
export const URI = '@nll/datum/DatumEither';
/**
 * @since 2.4.1
 */
export const initial = D.initial;
/**
 * @since 2.4.1
 */
export const pending = D.pending;
/**
 * @since 2.1.0
 */
export const success = (a) => D.replete(right(a));
/**
 * @since 2.1.0
 */
export const failure = (e) => D.replete(left(e));
/**
 * @since 3.4.0
 */
export const successR = (a) => toRefresh(D.replete(right(a)));
/**
 * @since 3.4.0
 */
export const failureR = (e) => toRefresh(D.replete(left(e)));
/**
 * @since 2.4.1
 */
export function constInitial() {
    return initial;
}
/**
 * @since 2.4.1
 */
export const constPending = () => pending;
/**
 * @since 2.7.0
 */
export const isInitial = D.isInitial;
/**
 * @since 2.7.0
 */
export const isPending = D.isPending;
/**
 * @since 2.7.0
 */
export const isRefresh = D.isRefresh;
/**
 * @since 2.7.0
 */
export const isReplete = D.isReplete;
/**
 * @since 2.7.0
 */
export const isValued = D.isValued;
/**
 * @since 2.7.0
 */
export const isRefreshLeft = (fea) => isRefresh(fea) && isLeft(fea.value);
/**
 * @since 2.7.0
 */
export const isRefreshRight = (fea) => isRefresh(fea) && isRight(fea.value);
/**
 * @since 2.7.0
 */
export const isRepleteLeft = (fea) => isReplete(fea) && isLeft(fea.value);
/**
 * @since 2.7.0
 */
export const isRepleteRight = (fea) => isReplete(fea) && isRight(fea.value);
/**
 * @since 2.1.0
 */
export const isSuccess = (fea) => isValued(fea) && isRight(fea.value);
/**
 * @since 2.1.0
 */
export const isFailure = (fea) => isValued(fea) && isLeft(fea.value);
/**
 * @since 2.1.0
 */
export const toRefresh = (fea) => D.fold(constPending, constPending, constant(fea), D.refresh)(fea);
/**
 * @since 2.7.0
 */
export const toReplete = (fea) => D.fold(constInitial, constInitial, D.replete, constant(fea))(fea);
/**
 * fromEither will remove the Lazy input in the next major release
 *
 * @since 2.2.0
 *
 * @deprecated
 */
export const fromEither = (e) => D.replete(e());
/**
 * fromEither2 will replace fromEither in the next major release
 *
 * @since 3.4.0
 *
 * @deprecated
 */
export const fromEither2 = (e) => D.replete(e);
/**
 * @since 2.2.0
 */
export const fromOption = (onNone) => (o) => D.replete(eitherFromOption(onNone)(o));
/**
 * Takes a nullable value, if the value is not nully, turn it into a `Success<A>`, otherwise `Initial`.
 *
 * @since 2.4.0
 */
export const fromNullable = (a) => (a === null || a === undefined ? initial : success(a));
/**
 * @since 2.7.0
 */
export const fold = (onInitial, onPending, onRefreshLeft, onRefreshRight, onRepleteLeft, onRepleteRight) => (fea) => pipe(fea, D.fold(onInitial, onPending, eitherFold(onRefreshLeft, onRefreshRight), eitherFold(onRepleteLeft, onRepleteRight)));
/**
 * @since 2.1.0
 */
export const refreshFold = (onInitial, onPending, onFailure, onSuccess) => (fea) => D.fold(onInitial, onPending, (e) => (isRight(e) ? onSuccess(e.right, true) : onFailure(e.left, true)), (e) => (isRight(e) ? onSuccess(e.right, false) : onFailure(e.left, false)))(fea);
/**
 * @since 2.3.0
 */
export const squash = (onNone, onFailure, onSuccess) => (fea) => D.fold(() => onNone(false), () => onNone(true), (e) => (isRight(e) ? onSuccess(e.right, true) : onFailure(e.left, true)), (e) => (isRight(e) ? onSuccess(e.right, false) : onFailure(e.left, false)))(fea);
/**
 * @since 3.4.0
 */
const traverseC = (F) => (ta, f) => fold(() => F.of(initial), () => F.of(pending), (e) => F.of(D.refresh(left(e))), (a) => F.map(f(a), flow(right, D.refresh)), (e) => F.of(D.replete(left(e))), (a) => F.map(f(a), flow(right, D.replete)))(ta);
/**
 * @since 3.4.0
 */
const sequenceC = (F) => (ta) => fold(() => F.of(initial), () => F.of(pending), (e) => F.of(D.refresh(left(e))), (a) => F.map(a, flow(right, D.refresh)), (e) => F.of(D.replete(left(e))), (a) => F.map(a, flow(right, D.replete)))(ta);
/**
 * @since 3.4.0
 */
const reduceC = (fa, b, f) => pipe(fa, fold(() => b, () => b, () => b, (a) => f(b, a), () => b, (a) => f(b, a)));
/**
 * @since 3.4.0
 */
const foldMapC = (M) => (fa, f) => fold(() => M.empty, () => M.empty, () => M.empty, f, () => M.empty, f)(fa);
/**
 * @since 3.4.0
 */
const reduceRightC = (fa, b, f) => fold(() => b, () => b, () => b, (a) => f(a, b), () => b, (a) => f(a, b))(fa);
/**
 * @since 2.0.0
 *
 * @deprecated Use standalone instances and instance factories
 */
export const datumEither = Object.assign(Object.assign({ URI }, getEitherM(D.datum)), { traverse: traverseC, sequence: sequenceC, reduce: reduceC, foldMap: foldMapC, reduceRight: reduceRightC });
// TODO: After we bump the min bound to >= 2.10, replace this with individual helper fns
const eitherTDatum = getEitherM(D.Monad);
/**
 * @since 3.5.0
 */
export const Functor = {
    URI,
    map: eitherTDatum.map
};
/**
 * @since 3.5.0
 */
export const Bifunctor = {
    URI,
    mapLeft: eitherTDatum.mapLeft,
    bimap: eitherTDatum.bimap
};
/**
 * @since 3.5.0
 *
 * Note: This instance agrees with the standalone Applicative/Chain/Monad instances but _disagrees_ with the deprecated `datum` mega-instance.
 */
export const Apply = Object.assign(Object.assign({}, Functor), { ap: eitherTDatum.ap });
/**
 * @since 3.5.0
 */
export const Chain = Object.assign(Object.assign({}, Apply), { chain: eitherTDatum.chain });
/**
 * @since 3.5.0
 */
export const Applicative = Object.assign(Object.assign({}, Apply), { of: eitherTDatum.of });
/**
 * @since 3.5.0
 */
export const Alt = Object.assign(Object.assign({}, Functor), { alt: eitherTDatum.alt });
/**
 * @since 3.5.0
 */
export const Alternative = Object.assign(Object.assign(Object.assign({}, Alt), Applicative), { zero: constInitial });
/**
 * @since 3.5.0
 */
export const Monad = Object.assign(Object.assign({}, Chain), { of: eitherTDatum.of });
/**
 * @since 3.5.0
 */
export const MonadThrow = Object.assign(Object.assign({}, Monad), { throwError: failure });
/**
 * @since 3.5.0
 */
export const Foldable = {
    URI,
    reduce: reduceC,
    reduceRight: reduceRightC,
    foldMap: foldMapC
};
/**
 * @since 3.5.0
 */
export const Traversable = Object.assign(Object.assign(Object.assign({}, Functor), Foldable), { sequence: sequenceC, traverse: traverseC });
/**
 * @since 3.2.0
 */
export const sequenceTuple = sequenceT(datumEither);
/**
 * @since 3.2.0
 */
export const sequenceStruct = sequenceS(datumEither);
/**
 * @since 3.2.0
 */
const { alt, ap, apFirst, apSecond, bimap, chain, chainFirst, flatten, foldMap, map, mapLeft, reduce, reduceRight, } = pipeable(datumEither);
const { ap: ap2 } = pipeable(Apply);
export { 
/**
 * @since 2.0.0
 */
alt, 
/**
 * @since 2.0.0
 *
 * @deprecated Does not agree with chain. This will be replaced in the next major release with the behavior of `ap2`
 */
ap, 
/**
 * @since 3.5.0
 *
 */
ap2, 
/**
 * @since 2.0.0
 */
apFirst, 
/**
 * @since 2.0.0
 */
apSecond, 
/**
 * @since 2.0.0
 */
bimap, 
/**
 * @since 2.0.0
 */
chain, 
/**
 * @since 2.0.0
 */
chainFirst, 
/**
 * @since 2.0.0
 */
flatten, 
/**
 * @since 2.0.0
 */
map, 
/**
 * @since 2.0.0
 */
mapLeft, 
/**
 * @since 3.4.0
 */
reduce, 
/**
 * @since 3.4.0
 */
foldMap, 
/**
 * @since 3.4.0
 */
reduceRight, };
//# sourceMappingURL=DatumEither.js.map